import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PayPalButton({ amount, onSuccess, onError, disabled }) {
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Fetch PayPal Client ID from backend
    const fetchClientId = async () => {
      try {
        const response = await axios.get(`${API}/api/paypal/client-id`);
        setClientId(response.data.client_id);
        setLoading(false);
      } catch (err) {
        console.error("Failed to get PayPal client ID:", err);
        setError("PayPal not configured. Please contact support.");
        setLoading(false);
      }
    };
    fetchClientId();
  }, []);

  const handleCreateOrder = async () => {
    console.log("PayPal createOrder triggered, amount:", amount);
    try {
      const response = await axios.post(`${API}/api/paypal/create-order`, {
        amount: amount
      });
      
      console.log("Order created:", response.data);
      
      if (response.data.order_id === "FREE") {
        // Handle free orders
        onSuccess({ transaction_id: "FREE", status: "COMPLETED" });
        return null;
      }
      
      return response.data.order_id;
    } catch (err) {
      console.error("Failed to create order:", err);
      onError(err.response?.data?.detail || "Failed to create PayPal order");
      throw err;
    }
  };

  const handleApprove = async (data) => {
    console.log("PayPal onApprove called with orderID:", data.orderID);
    setProcessing(true);
    
    try {
      const response = await axios.post(`${API}/api/paypal/capture-order/${data.orderID}`);
      console.log("Capture response:", response.data);
      
      if (response.data.status === "COMPLETED") {
        console.log("Payment successful!");
        onSuccess({
          transaction_id: response.data.transaction_id,
          amount: response.data.amount,
          status: response.data.status
        });
      } else {
        console.log("Payment not completed, status:", response.data.status);
        onError("Payment was not completed. Please try again.");
      }
    } catch (err) {
      console.error("Failed to capture order:", err);
      console.error("Error details:", err.response?.data);
      onError(err.response?.data?.detail || "Failed to capture payment. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin text-[#52BFB8]" />
        <span className="ml-2 text-gray-500">Loading PayPal...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
        <p className="text-yellow-700 text-sm">PayPal is not configured. Please contact support.</p>
      </div>
    );
  }

  return (
    <PayPalScriptProvider 
      options={{ 
        clientId: clientId,
        currency: "USD",
        intent: "capture",
        "enable-funding": "paylater,card",
        "disable-funding": "",
        components: "buttons"
      }}
    >
      <div className="paypal-button-container" data-testid="paypal-smart-buttons">
        {processing ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#52BFB8]" />
            <span className="ml-2 text-gray-500">Processing payment...</span>
          </div>
        ) : (
          <PayPalButtons
            key={`paypal-${amount}`}
            style={{
              layout: "vertical",
              color: "blue",
              shape: "rect",
              label: "pay",
              height: 45
            }}
            disabled={disabled}
            forceReRender={[amount]}
            createOrder={handleCreateOrder}
            onApprove={handleApprove}
            onError={(err) => {
              console.error("PayPal onError triggered:", err);
              setProcessing(false);
              onError("PayPal encountered an error. Please try again.");
            }}
            onCancel={(data) => {
              console.log("PayPal onCancel triggered:", data);
              setProcessing(false);
            }}
          />
        )}
      </div>
    </PayPalScriptProvider>
  );
}
